import { and, desc, eq, getTableColumns, inArray, isNotNull, isNull, ne, or, sql } from "drizzle-orm";

import type { WsItem } from "@storyboard/web-client";
import { db } from "@/db/db";
import { executeBuiltQuery, executeQuery, Tx } from "@/db/drizzleUtils";
import {
  account,
  audioEncoding,
  callRecord,
  displayArtifact,
  feed,
  feedPreference,
  file,
  item,
  link,
  permission,
  transcription,
  workspaceCommandAlias,
  workspaceMembership,
} from "@/db/schema";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emptyQuery } from "@/models/emptyQuery.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { logQuery } from "@/models/logQuery.ts";
import { chunkWithMinSize } from "@/utils";

export async function markItemsContentLoaded(feedItemIds: string[], deferEmit = false): Promise<void> {
  if (!feedItemIds?.length) return;
  await db
    .update(item)
    .set({
      loadedContent: true,
    })
    .where(inArray(item.id, feedItemIds))
    .execute();
  if (!deferEmit) {
    emitTableUpdates(["item"]);
  }
}

export async function resetAllItemsLoadedContent(): Promise<void> {
  await db.update(item).set({ loadedContent: false }).execute();
  emitTableUpdates(["item"]);
}

export async function resetOrphanedLoadedContent(feedId: string): Promise<void> {
  await db.execute(sql`
    UPDATE ${item}
    SET "loadedContent" = false
    WHERE ${item.feedId} = ${feedId}
      AND ${item.loadedContent} = true
      AND NOT EXISTS (SELECT 1 FROM ${file} WHERE ${file.contentId} = ${item.contentId})
      AND NOT EXISTS (SELECT 1 FROM ${transcription} WHERE ${transcription.contentId} = ${item.contentId})
      AND NOT EXISTS (SELECT 1 FROM ${audioEncoding} WHERE ${audioEncoding.contentId} = ${item.contentId})
      AND NOT EXISTS (SELECT 1 FROM ${link} WHERE ${link.contentId} = ${item.contentId})
      AND NOT EXISTS (SELECT 1 FROM ${displayArtifact} WHERE ${displayArtifact.contentId} = ${item.contentId})
      AND NOT EXISTS (SELECT 1 FROM ${callRecord} WHERE ${callRecord.contentId} = ${item.contentId})
  `);
  emitTableUpdates(["item"]);
}

async function checkCommandAliasesForPairs(tx: Tx, pairs: { feedId: string; accountId: string }[]) {
  if (pairs.length === 0) return new Map<string, boolean>();

  const conditions = pairs.map((pair) =>
    and(eq(workspaceCommandAlias.feedId, pair.feedId), eq(workspaceMembership.accountId, pair.accountId)),
  );

  const results = await executeBuiltQuery<{
    feedId: string;
    accountId: string;
  }>(
    db
      .select({
        feedId: workspaceCommandAlias.feedId,
        accountId: workspaceMembership.accountId,
      })
      .from(workspaceCommandAlias)
      .innerJoin(workspaceMembership, eq(workspaceCommandAlias.workspaceMembershipId, workspaceMembership.id))
      .where(or(...conditions)),
    "checkCommandAliasesForPairs",
    tx,
  );

  return new Map<string, boolean>(results.map((row) => [`${row.feedId}:${row.accountId}`, true]));
}

// TODO: It is possible to inform the item via the backend about the user status
async function performBulkUpsertWsItems(itemRecords: WsItem[], fromAppSync = false, tx: Tx, deferEmit = false) {
  const allResults = [] as (typeof item)["$inferSelect"][];

  const pairs = itemRecords.map((itemRecord) => ({
    feedId: itemRecord.feedId,
    accountId: itemRecord.accountId,
  }));

  const aliasMap = await checkCommandAliasesForPairs(tx, pairs);

  const accountIds = [...new Set(itemRecords.map((item) => item.accountId))];

  const botAccounts = await executeBuiltQuery<{ id: string }>(
    db
      .select({ id: account.id })
      .from(account)
      .where(and(inArray(account.id, accountIds), eq(account.bot, true))),
    "getBotAccounts",
    tx,
  );

  const botAccountMap = new Map<string, boolean>(botAccounts.map((acc) => [acc.id, true]));

  const allDataSet = itemRecords.map((itemRecord) => {
    const isFromAliasMemberBoolean = aliasMap.has(`${itemRecord.feedId}:${itemRecord.accountId}`);
    const isFromBotBoolean = botAccountMap.has(itemRecord.accountId);

    return {
      id: itemRecord.id,
      feedId: itemRecord.feedId,
      accountId: itemRecord.accountId,
      contentId: itemRecord.contentId,
      createdAt: itemRecord.createdAt,
      updatedAt: itemRecord.createdAt,
      deletedAt: itemRecord.deletedAt,
      groupId: itemRecord.groupId,
      isSilent: itemRecord.isSilent || false,
      fromAppSync: fromAppSync,
      isFromAliasMember: isFromAliasMemberBoolean,
      isFromBot: isFromBotBoolean,
      firstInsert: true,
      loadedContent: false,
      unread: false,
      unreadNonMutable: false,
      status: "Completed",
      hasRegisteredSeenEvent: false,
    } as (typeof item)["$inferInsert"];
  });
  const deduped = deduplicateByKeyValue(allDataSet, (item) => item.id);
  for (const dataSet of chunkWithMinSize(deduped, 100)) {
    const partialResults = await executeBuiltQuery<(typeof item)["$inferSelect"]>(
      db
        .insert(item)
        .values(dataSet)
        .onConflictDoUpdate({
          target: item.id,
          set: {
            firstInsert: false,
            deletedAt: sql`excluded."deletedAt"`,
            groupId: sql`excluded."groupId"`,
          },
        })
        .returning({
          ...getTableColumns(item),
        }),
      "bulkUpsertWsItems",
      tx,
    );
    allResults.push(...partialResults);
  }

  const feedUpdatesMap = new Map<string, { maxCreatedAt: string; maxAliasCreatedAt?: string }>();

  for (const itemRecord of deduped) {
    const feedId = itemRecord.feedId as string;
    const currentEntry = feedUpdatesMap.get(feedId) || {
      maxCreatedAt: null as unknown as string,
      maxAliasCreatedAt: null as unknown as string,
    };

    if (!currentEntry.maxCreatedAt || (itemRecord.createdAt as string) > currentEntry.maxCreatedAt) {
      currentEntry.maxCreatedAt = itemRecord.createdAt as string;
    }

    if ((itemRecord as any).isFromAliasMember) {
      if (!currentEntry.maxAliasCreatedAt || (itemRecord.createdAt as string) > currentEntry.maxAliasCreatedAt) {
        currentEntry.maxAliasCreatedAt = itemRecord.createdAt as string;
      }
    }

    feedUpdatesMap.set(feedId, currentEntry);
  }

  // Use SQL NULL when a feed batch has no max timestamp. GREATEST ignores NULL, but
  // GREATEST(NULL, '-infinity') yields -infinity (sentinel was meant as "no value"), which
  // then breaks JS Date / PGlite when read back.
  const allFeedUpdateTuples = Array.from(feedUpdatesMap.entries()).map(
    ([fid, { maxCreatedAt, maxAliasCreatedAt }]) =>
      sql`(${sql.join(
        [
          sql`${fid}`,
          maxCreatedAt != null ? sql`${maxCreatedAt}` : sql`NULL`,
          maxAliasCreatedAt != null ? sql`${maxAliasCreatedAt}` : sql`NULL`,
        ],
        sql`, `,
      )})`,
  );

  for (const feedUpdateTuples of chunkWithMinSize(allFeedUpdateTuples, 2000)) {
    if (feedUpdateTuples.length > 0) {
      const updateSql = sql`
        WITH feeds_to_update (id, max_created_at, max_alias_created_at) AS (
          VALUES ${sql.join(feedUpdateTuples, sql`, `)}
        )
        UPDATE ${feed}
        SET 
          "latestActivity" = GREATEST(
              ${feed}."latestActivity", 
              feeds_to_update.max_created_at::timestamptz
            ),
          "latestAliasActivity" = GREATEST(
            ${feed}."latestAliasActivity", 
            feeds_to_update.max_alias_created_at::timestamptz
          )
        FROM feeds_to_update
        WHERE ${feed}.id = feeds_to_update.id
      `;
      await executeQuery(updateSql, "bulkUpsertWsItems", tx);
    }
  }
  if (!deferEmit) {
    emitTableUpdates(["item"]);
  }
  return allResults;
}

export async function bulkUpsertItemAndCalculateMembership(
  itemRecords: WsItem[],
  options?: { fromAppSync?: boolean; tx?: Tx; deferEmit?: boolean },
) {
  const fromAppSync = options?.fromAppSync ?? false;
  const tx = options?.tx;
  const deferEmit = options?.deferEmit ?? false;
  const results = tx
    ? await performBulkUpsertWsItems(itemRecords, fromAppSync, tx, deferEmit)
    : await db.$client.transaction(
        async (txInner) =>
          await performBulkUpsertWsItems(itemRecords, fromAppSync, txInner as unknown as Tx, deferEmit),
      );
  // Don't emit here - performBulkUpsertWsItems handles emit based on deferEmit flag
  return results;
}

export function unreadFeedIdsQuery({
  myCurrentWorkspaceMembershipId,
  requireNotificationPreference = false,
  limit,
  teamAliases,
}: {
  myCurrentWorkspaceMembershipId?: string;
  requireNotificationPreference?: boolean;
  limit?: number;
  teamAliases?: string[];
}) {
  if (!myCurrentWorkspaceMembershipId) {
    return emptyQuery as unknown as typeof query;
  }
  const selectFields =
    limit !== undefined ? { feedId: item.feedId, latestActivity: feed.latestActivity } : { feedId: item.feedId };

  const query = db
    .selectDistinct(selectFields)
    .from(item)
    .innerJoin(feed, eq(feed.id, item.feedId))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, myCurrentWorkspaceMembershipId),
      ),
    );

  // Only filter by notification preference if explicitly required
  if (requireNotificationPreference) {
    query.innerJoin(
      feedPreference,
      and(
        eq(feedPreference.feedId, feed.id),
        eq(feedPreference.workspaceMembershipId, myCurrentWorkspaceMembershipId),
        ne(feedPreference.pushNotificationPreference, "none"),
      ),
    );
  }

  const hasTeamFilter = teamAliases && teamAliases.length > 0;

  const filters = [eq(item.unread, true), isNull(item.deletedAt)];
  if (requireNotificationPreference) {
    filters.push(eq(feed.isDm, false), isNotNull(feed.title));
  }

  if (hasTeamFilter) {
    const aliasSub = db
      .selectDistinctOn([workspaceCommandAlias.feedId], {
        feedId: workspaceCommandAlias.feedId,
        alias: workspaceCommandAlias.alias,
      })
      .from(workspaceCommandAlias)
      .orderBy(workspaceCommandAlias.feedId)
      .as("unreadAlias");

    query.leftJoin(aliasSub, eq(aliasSub.feedId, feed.id));
    query.where(and(...filters, or(eq(feed.isAliasFeed, false), inArray(aliasSub.alias, teamAliases))));
  } else {
    query.where(and(...filters));
  }

  if (limit !== undefined) {
    query.orderBy(desc(feed.latestActivity));
    query.limit(limit);
  }

  logQuery("Unread Feed Ids", query);
  return query;
}

export const fetchParentIds = async (contentIds: string[]) => {
  const parentIds = await db.select({ id: item.id }).from(item).where(inArray(item.contentId, contentIds));
  if (parentIds?.length > 0) {
    return parentIds.map((parent) => parent.id);
  }
  return [];
};

export const driverUnreadCount = ({
  workspaceId,
  workspaceMembershipId,
  teamAliases,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  teamAliases?: string[];
}) => {
  if (!workspaceId || !workspaceMembershipId) return emptyQuery as unknown as ReturnType<typeof buildUnreadCountQuery>;
  return buildUnreadCountQuery({ workspaceId, workspaceMembershipId, isAliasFeed: true, teamAliases });
};

export const channelUnreadCount = ({
  workspaceId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
}) => {
  if (!workspaceId || !workspaceMembershipId) return emptyQuery as unknown as ReturnType<typeof buildUnreadCountQuery>;
  return buildUnreadCountQuery({ workspaceId, workspaceMembershipId, isAliasFeed: false });
};

function buildUnreadCountQuery({
  workspaceId,
  workspaceMembershipId,
  isAliasFeed,
  teamAliases,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  isAliasFeed: boolean;
  teamAliases?: string[];
}) {
  const hasTeamFilter = isAliasFeed && teamAliases && teamAliases.length > 0;

  const query = db
    .select({ count: sql<number>`cast(count(distinct ${item.feedId}) as int)`.as("count") })
    .from(item)
    .innerJoin(feed, eq(feed.id, item.feedId))
    .innerJoin(
      permission,
      and(
        eq(permission.feedId, feed.id),
        eq(permission.name, "read"),
        eq(permission.enabled, true),
        eq(permission.workspaceMembershipId, workspaceMembershipId),
      ),
    );

  if (hasTeamFilter) {
    const aliasSub = db
      .selectDistinctOn([workspaceCommandAlias.feedId], {
        feedId: workspaceCommandAlias.feedId,
        alias: workspaceCommandAlias.alias,
      })
      .from(workspaceCommandAlias)
      .orderBy(workspaceCommandAlias.feedId)
      .as("countAlias");

    query.innerJoin(aliasSub, eq(aliasSub.feedId, feed.id));
    query.where(
      and(
        eq(feed.workspaceId, workspaceId),
        eq(feed.isAliasFeed, isAliasFeed),
        eq(item.unread, true),
        isNull(item.deletedAt),
        inArray(aliasSub.alias, teamAliases),
      ),
    );
  } else {
    query.where(
      and(
        eq(feed.workspaceId, workspaceId),
        eq(feed.isAliasFeed, isAliasFeed),
        eq(item.unread, true),
        isNull(item.deletedAt),
      ),
    );
  }

  return query;
}
